import { useMemo, useState } from 'react';
import {
    AlertTriangle,
    Upload,
    ChevronDown,
    Users,
    BookOpen,
    Calculator,
    Info,
} from 'lucide-react';

/**
 * Vue Établissement — affichée pour le prof délégué d'affectation.
 *
 * Sections empilées :
 *   1. Bandeau d'alerte info (orange, sans CTA) — signal visuel "N classes vides"
 *      qui renvoie vers le seul CTA "Mettre à jour l'établissement" en dessous
 *   2. Card "Mettre à jour l'établissement" — CTA unique : fichier complet attendu,
 *      la modale détecte automatiquement classes vides + nouveaux arrivants
 *   3. Filtres (Toutes / Vides)
 *   4. Liste des classes groupées par niveau, repliables, avec prof maths/français référent
 *
 * Niveau détecté via le 1er caractère du nom (ex: "6HORTEN" → '6', "501 ULIS" → '5'),
 * cohérent avec RootProf.jsx l. 81.
 */

const NIVEAUX_ORDER = ['6', '5', '4', '3'];
const NIVEAUX_LABELS = { '6': '6ème', '5': '5ème', '4': '4ème', '3': '3ème' };

const formatProfName = (prof) =>
    prof ? `${prof.prenom || ''} ${prof.nom || ''}`.trim() : null;

function EtablissementView({ classes = [], professeurs = [], classesVides = [], onImport }) {

    const totalClasses = classes.length;
    const filledClasses = totalClasses - classesVides.length;
    const coveragePct = totalClasses > 0 ? Math.round((filledClasses / totalClasses) * 100) : 0;

    // Profs référents indexés par classe — un seul parcours
    const profsByClasse = useMemo(() => {
        const map = new Map();
        for (const classe of classes) {
            const profMaths = professeurs.find(p => p.matiere === 'maths' && p.classes?.includes(classe.id));
            const profFr = professeurs.find(p => p.matiere === 'Français' && p.classes?.includes(classe.id));
            map.set(classe.id, {
                profMathsName: formatProfName(profMaths),
                profFrName: formatProfName(profFr),
            });
        }
        return map;
    }, [classes, professeurs]);

    // Groupement par niveau (tri alpha + classes vides en haut de chaque niveau)
    const groupedByNiveau = useMemo(() => {
        const groups = {};
        for (const c of classes) {
            const niv = c.nom?.[0];
            if (!NIVEAUX_ORDER.includes(niv)) continue;
            if (!groups[niv]) groups[niv] = [];
            groups[niv].push(c);
        }
        for (const niv of Object.keys(groups)) {
            groups[niv].sort((a, b) => {
                const aVide = (a.nombre_eleves || 0) === 0;
                const bVide = (b.nombre_eleves || 0) === 0;
                if (aVide && !bVide) return -1;
                if (!aVide && bVide) return 1;
                return (a.nom || '').localeCompare(b.nom || '');
            });
        }
        return groups;
    }, [classes]);

    const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'empty'
    const [collapsedNiveaux, setCollapsedNiveaux] = useState(new Set());

    const toggleNiveau = (niv) => {
        setCollapsedNiveaux(prev => {
            const next = new Set(prev);
            if (next.has(niv)) next.delete(niv);
            else next.add(niv);
            return next;
        });
    };

    // Filtrage côté affichage
    const filteredGrouped = useMemo(() => {
        const result = {};
        for (const niv of NIVEAUX_ORDER) {
            const list = groupedByNiveau[niv];
            if (!list) continue;
            const filtered = activeFilter === 'empty'
                ? list.filter(c => (c.nombre_eleves || 0) === 0)
                : list;
            if (filtered.length > 0) result[niv] = filtered;
        }
        return result;
    }, [groupedByNiveau, activeFilter]);

    const firstEmpty = classesVides[0];
    const firstEmptyNiveau = firstEmpty?.nom?.[0];
    const firstEmptyProfMaths = firstEmpty
        ? profsByClasse.get(firstEmpty.id)?.profMathsName
        : null;

    return (
        <div className="space-y-4">

            {/* 1. Bandeau d'alerte info (sans CTA) — signal visuel pour les classes vides.
                 L'action passe par "Mettre à jour l'établissement" ci-dessous. */}
            {classesVides.length > 0 && firstEmpty && (
                <div role="alert" className="bg-orange-50 border border-amber-500 rounded-xl px-4 py-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                        <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[14px] font-medium text-amber-900 m-0">
                            {classesVides.length} classe{classesVides.length > 1 ? 's' : ''} sans élève
                        </p>
                        <p className="text-[13px] text-amber-700 mt-0.5">
                            <strong className="font-medium">{firstEmpty.nom}</strong>
                            {firstEmptyNiveau && NIVEAUX_LABELS[firstEmptyNiveau] && ` · ${NIVEAUX_LABELS[firstEmptyNiveau]}`}
                            {firstEmptyProfMaths && ` · ${firstEmptyProfMaths}`}
                            {classesVides.length > 1 && ` (et ${classesVides.length - 1} autre${classesVides.length > 2 ? 's' : ''})`}
                            <span className="ml-1 italic">— utilisez <strong className="font-medium">Mettre à jour l'établissement</strong> ci-dessous avec votre fichier complet.</span>
                        </p>
                    </div>
                </div>
            )}

            {/* 2. Card "Mettre à jour l'établissement" — CTA unique.
                 Le fichier complet de l'établissement est toujours attendu : la modale
                 détecte d'elle-même les classes à peupler et les nouveaux arrivants. */}
            <div className="bg-white border border-blue-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-blue-100 rounded-md flex items-center justify-center shrink-0">
                            <Users className="w-[18px] h-[18px] text-blue-600" />
                        </div>
                        <div>
                            <p className="text-[14px] font-medium text-slate-800 m-0">
                                Mettre à jour l'établissement
                            </p>
                            <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
                                Importez le fichier Excel <strong className="font-medium">complet</strong> de votre établissement. TanoEd détectera automatiquement les classes à remplir et les nouveaux arrivants dans les classes existantes.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onImport}
                        className="bg-blue-500 hover:bg-blue-600 text-white border-0 px-3.5 py-2 rounded-md text-[13px] font-medium cursor-pointer flex items-center gap-1.5 shrink-0 transition-colors"
                    >
                        <Upload className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Mettre à jour
                    </button>
                </div>

                <div className="bg-slate-50 rounded-md px-3 py-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[12px] text-slate-500">Couverture des effectifs</span>
                        <span className="text-[12px] text-slate-800 font-medium">
                            {filledClasses} / {totalClasses} classes peuplées
                        </span>
                    </div>
                    <div className="bg-slate-200 h-1.5 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${coveragePct >= 80 ? 'bg-green-500' : 'bg-amber-500'}`}
                            style={{ width: `${coveragePct}%` }}
                        />
                    </div>
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500 italic leading-relaxed">
                        <Info className="w-3 h-3 shrink-0 mt-0.5 not-italic" />
                        <span>Si une classe manque dans la liste, c'est que son prof référent ne s'est pas encore connecté à TanoEd.</span>
                    </div>
                </div>
            </div>

            {/* 3. Filtres */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] text-slate-500 font-medium">Filtrer :</span>
                <FilterPill
                    label={`Toutes (${totalClasses})`}
                    active={activeFilter === 'all'}
                    onClick={() => setActiveFilter('all')}
                />
                {classesVides.length > 0 && (
                    <FilterPill
                        label={`⚠ Vides (${classesVides.length})`}
                        active={activeFilter === 'empty'}
                        variant="warning"
                        onClick={() => setActiveFilter('empty')}
                    />
                )}
            </div>

            {/* 4. Classes groupées par niveau */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
                {Object.entries(filteredGrouped).map(([niv, list], idx) => (
                    <NiveauSection
                        key={niv}
                        niveau={niv}
                        niveauLabel={NIVEAUX_LABELS[niv]}
                        classes={list}
                        totalEleves={list.reduce((sum, c) => sum + (c.nombre_eleves || 0), 0)}
                        nbVides={list.filter(c => (c.nombre_eleves || 0) === 0).length}
                        collapsed={collapsedNiveaux.has(niv)}
                        onToggle={() => toggleNiveau(niv)}
                        profsByClasse={profsByClasse}
                        isFirst={idx === 0}
                    />
                ))}
                {Object.keys(filteredGrouped).length === 0 && (
                    <div className="p-8 text-center text-slate-500 text-[13px]">
                        Aucune classe ne correspond à ce filtre.
                    </div>
                )}
            </div>
        </div>
    );
}

// =====================================================
// Sous-composants
// =====================================================

function FilterPill({ label, active, variant = 'default', onClick }) {
    if (active) {
        return (
            <button
                onClick={onClick}
                className="text-[12px] px-3 py-1 rounded-full bg-blue-500 text-white border-0 cursor-pointer font-medium"
            >
                {label}
            </button>
        );
    }
    if (variant === 'warning') {
        return (
            <button
                onClick={onClick}
                className="text-[12px] px-3 py-1 rounded-full bg-orange-50 text-amber-700 border border-amber-300 cursor-pointer font-medium hover:bg-orange-100 transition-colors"
            >
                {label}
            </button>
        );
    }
    return (
        <button
            onClick={onClick}
            className="text-[12px] px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors"
        >
            {label}
        </button>
    );
}

function NiveauSection({
    niveau, niveauLabel, classes, totalEleves, nbVides,
    collapsed, onToggle, profsByClasse, isFirst,
}) {
    return (
        <div className={isFirst ? '' : 'border-t border-slate-200'}>
            <button
                onClick={onToggle}
                aria-expanded={!collapsed}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
            >
                <div className="flex items-center gap-2.5">
                    <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-500 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                        strokeWidth={2.5}
                    />
                    <span className="text-[14px] font-medium text-slate-800">{niveauLabel}</span>
                    <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-[11px] font-medium">
                        {classes.length} classe{classes.length > 1 ? 's' : ''}
                    </span>
                    {nbVides > 0 && (
                        <span className="bg-orange-50 text-amber-700 px-2 py-0.5 rounded-full text-[11px] font-medium">
                            ⚠ {nbVides} vide{nbVides > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <span className="text-[12px] text-slate-500">{totalEleves} élève{totalEleves > 1 ? 's' : ''}</span>
            </button>

            {!collapsed && (
                <div>
                    <div className="grid grid-cols-[110px_80px_1fr_1fr_80px] gap-3 px-4 py-2 bg-slate-50/50 text-[11px] text-slate-400 uppercase tracking-wider font-medium border-b border-slate-100">
                        <span>Classe</span>
                        <span>Élèves</span>
                        <span className="flex items-center gap-1.5">
                            <Calculator className="w-3 h-3" /> Maths
                        </span>
                        <span className="flex items-center gap-1.5">
                            <BookOpen className="w-3 h-3" /> Français
                        </span>
                        <span></span>
                    </div>
                    {classes.map((c, idx) => (
                        <ClasseRow
                            key={c.id}
                            classe={c}
                            isLast={idx === classes.length - 1}
                            profMaths={profsByClasse.get(c.id)?.profMathsName}
                            profFr={profsByClasse.get(c.id)?.profFrName}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ClasseRow({ classe, isLast, profMaths, profFr }) {
    const isVide = (classe.nombre_eleves || 0) === 0;
    const baseClasses = `grid grid-cols-[110px_80px_1fr_1fr_80px] gap-3 px-4 py-3 items-center transition-colors ${isLast ? '' : 'border-b border-slate-100'}`;

    if (isVide) {
        return (
            <div className={`${baseClasses} bg-amber-50/60`}>
                <span className="text-[13px] font-medium text-slate-800 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    {classe.nom}
                </span>
                <span className="text-[13px] text-amber-700 font-medium">0 élève</span>
                <span className="text-[13px] text-slate-600">
                    {profMaths || <em className="text-slate-400">Pas référencé</em>}
                </span>
                <span className="text-[13px] text-slate-600">
                    {profFr || <em className="text-slate-400">Pas référencé</em>}
                </span>
                <span></span>
            </div>
        );
    }

    return (
        <div className={`${baseClasses} hover:bg-slate-50`}>
            <span className="text-[13px] font-medium text-slate-800">{classe.nom}</span>
            <span className="text-[13px] text-slate-500">{classe.nombre_eleves}</span>
            <span className="text-[13px] text-slate-600">
                {profMaths || <em className="text-slate-400">Pas référencé</em>}
            </span>
            <span className="text-[13px] text-slate-600">
                {profFr || <em className="text-slate-400">Pas référencé</em>}
            </span>
            <span></span>
        </div>
    );
}

export default EtablissementView;
